import './DroneList.css';
import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModemCard, { DroneDensity } from './ModemCard';
import { DRONE_FILTERS, DroneFilterId, DroneFilterState, DroneGroupId } from '../../hooks/useDroneFilter';

interface DroneListProps {
    filter: DroneFilterState;
    selectedImei: string | null;
    onSelect: (imei: string) => void;
    /** Console держит ссылки на карточки, чтобы скроллить к ним по клику на маркере. */
    registerCard: (imei: string, el: HTMLDivElement | null) => void;
    /** Закреплённый низ панели — туда уезжает ConsoleBindCard. */
    footer?: ReactNode;
    /** Первая строка шапки панели — индикатор связи с сервером. */
    status?: ReactNode;
}

const DENSITY_KEY = 'console.droneList.density';
const COLLAPSED_KEY = 'console.droneList.collapsed';

// Офлайн-группа свёрнута, пока пользователь не решил иначе.
const DEFAULT_COLLAPSED: DroneGroupId[] = ['offline'];

const GROUP_LABEL: Record<DroneGroupId, string> = {
    online: 'group_online',
    offline: 'group_offline'
};

const FILTER_LABEL: Record<DroneFilterId, string> = {
    all: 'filter_all',
    online: 'filter_online',
    arm: 'filter_arm',
    update: 'filter_update'
};

// localStorage недоступен в приватном режиме и при заблокированных данных сайта,
// а чтение может и бросить — поэтому каждый доступ обёрнут.
function readStored(key: string): string | null {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeStored(key: string, value: string): void {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // Память списка — удобство, а не функциональность: молча работаем без неё.
    }
}

function readDensity(): DroneDensity {
    return readStored(DENSITY_KEY) === 'compact' ? 'compact' : 'normal';
}

function readCollapsed(): DroneGroupId[] {
    const raw = readStored(COLLAPSED_KEY);
    if (raw === null) {
        return DEFAULT_COLLAPSED;
    }
    try {
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return DEFAULT_COLLAPSED;
        }
        return parsed.filter((id): id is DroneGroupId => id === 'online' || id === 'offline');
    } catch {
        return DEFAULT_COLLAPSED;
    }
}

function DroneList({ filter, selectedImei, onSelect, registerCard, footer, status }: DroneListProps): ReactNode {
    const { t } = useTranslation(['console']);

    const [density, setDensity] = useState<DroneDensity>(readDensity);
    const [collapsed, setCollapsed] = useState<DroneGroupId[]>(readCollapsed);

    const toggleDensity = useCallback((): void => {
        setDensity((current) => {
            const next: DroneDensity = current === 'compact' ? 'normal' : 'compact';
            writeStored(DENSITY_KEY, next);
            return next;
        });
    }, []);

    const toggleGroup = useCallback((id: DroneGroupId): void => {
        setCollapsed((current) => {
            const next = current.includes(id)
                ? current.filter((g) => g !== id)
                : [...current, id];
            writeStored(COLLAPSED_KEY, JSON.stringify(next));
            return next;
        });
    }, []);

    const resetFilters = useCallback((): void => {
        filter.setQuery('');
        filter.setFilter('all');
    }, [filter]);

    const isEmptyAccount = filter.total === 0;
    const isNothingFound = !isEmptyAccount && filter.visibleCount === 0;
    const densityHint = density === 'compact' ? t('density_to_normal') : t('density_to_compact');

    return (
        <div className={'drone-list drone-list-' + density}>
            <div className='drone-list-head'>
                {status}
                <input
                    className='drone-list-search'
                    type='search'
                    value={filter.query}
                    onChange={(e) => filter.setQuery(e.target.value)}
                    placeholder={t('search_placeholder')}
                    aria-label={t('search_label')}
                />
                <div className='drone-list-chips'>
                    {DRONE_FILTERS.map((id) => (
                        <button
                            key={id}
                            type='button'
                            className={'drone-list-chip' + (filter.filter === id ? ' drone-list-chip-active' : '')}
                            onClick={() => filter.setFilter(id)}
                            aria-pressed={filter.filter === id}
                        >
                            <span className='drone-list-chip-label'>{t(FILTER_LABEL[id])}</span>
                            <span className='drone-list-chip-count'>{filter.counts[id]}</span>
                        </button>
                    ))}
                    <button
                        type='button'
                        className='drone-list-density'
                        onClick={toggleDensity}
                        title={densityHint}
                        aria-label={densityHint}
                    >
                        <span className='drone-list-density-icon' aria-hidden='true' />
                    </button>
                </div>
            </div>

            <div className='drone-list-body'>
                {isEmptyAccount &&
                    <p className='drone-list-empty'>{t('empty_no_drones')}</p>
                }

                {isNothingFound &&
                    <div className='drone-list-empty'>
                        <p>{t('empty_nothing_found')}</p>
                        <button type='button' className='drone-list-reset' onClick={resetFilters}>
                            {t('empty_reset')}
                        </button>
                    </div>
                }

                {filter.groups.map((group) => {
                    const isCollapsed = collapsed.includes(group.id);
                    return (
                        <section className='drone-list-group' key={group.id}>
                            <button
                                type='button'
                                className={'drone-list-group-head' + (isCollapsed ? ' drone-list-group-collapsed' : '')}
                                onClick={() => toggleGroup(group.id)}
                                aria-expanded={!isCollapsed}
                                title={isCollapsed ? t('group_expand') : t('group_collapse')}
                            >
                                <span className='drone-list-group-caret' aria-hidden='true' />
                                <span className='drone-list-group-name'>{t(GROUP_LABEL[group.id])}</span>
                                <span className='drone-list-group-count'>{group.drones.length}</span>
                            </button>

                            {!isCollapsed &&
                                <div className='drone-list-group-body'>
                                    {group.drones.map((d) => (
                                        <div
                                            className='console-modem-card-wrap'
                                            key={d.imei}
                                            ref={(el) => registerCard(d.imei, el)}
                                        >
                                            <ModemCard
                                                imei={d.imei}
                                                modemName={d.name}
                                                isOnline={d.isOnline}
                                                isActive={selectedImei === d.imei}
                                                version={d.version}
                                                update={d.isLastFirmware}
                                                upgrade={d.upgradable}
                                                isArm={d.isArm}
                                                mavType={d.mavType}
                                                density={density}
                                                onClick={onSelect}
                                            />
                                        </div>
                                    ))}
                                </div>
                            }
                        </section>
                    );
                })}
            </div>

            {footer &&
                <div className='drone-list-footer'>{footer}</div>
            }
        </div>
    );
}

export default DroneList;
